import PropTypes from "prop-types";
import { usePermissions } from "../../contexts/PermissionContext";

/**
 * Declarative permission gate.
 *
 * Usage:
 *   <Can permission={P.Team.View}>          — single key
 *   <Can all={[P.Account.View, P.Team.View]}>  — every key required
 *   <Can any={[P.Ari.View, P.Reservations.View]}> — at least one required
 *
 * Gate on leaf keys, not "x.*" wildcards. The matcher expands the requested
 * key's prefixes against the grant set, so a request for "ari.*" only passes
 * when the role literally grants "ari.*" — holding every child of "ari" is not
 * enough. A request for "ari.view" passes on either "ari.view" or "ari.*".
 */
const Can = ({
  permission = null,
  all = null,
  any = null,
  fallback = null,
  children,
}) => {
  const { hasPermission, loading } = usePermissions();

  if (loading) return null;

  let allowed = false;
  if (permission) {
    allowed = hasPermission(permission);
  } else if (all) {
    allowed = all.every(hasPermission);
  } else if (any) {
    allowed = any.some(hasPermission);
  }

  return allowed ? children : fallback;
};

Can.propTypes = {
  permission: PropTypes.string,
  all: PropTypes.arrayOf(PropTypes.string),
  any: PropTypes.arrayOf(PropTypes.string),
  fallback: PropTypes.node,
  children: PropTypes.node.isRequired,
};

export default Can;
